import { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../utils/apiClient';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';

interface GameConfigRow {
  id: string;
  date: string;
  category: string;
  slug: string;
  published: boolean;
  created_at: string;
}

interface PastGamesProps {
  gameType: string;
  category?: string;
}

export const PastGames = ({ gameType, category }: PastGamesProps) => {
  const [rows, setRows] = useState<GameConfigRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        _sort: 'date',
        _order: 'DESC',
        _start: String(page * rowsPerPage),
        _end: String((page + 1) * rowsPerPage),
        game_type: gameType,
      });
      if (category) params.set('category', category);

      const res = await fetch(`${API_BASE}/admin/api/game-configs?${params}`, {
        credentials: 'include',
      });

      if (!res.ok) throw new Error('Failed to fetch past games');

      const contentRange = res.headers.get('Content-Range');
      if (contentRange) {
        const match = contentRange.match(/\/(\d+)/);
        if (match) setTotal(parseInt(match[1]));
      }

      const data = await res.json();
      setRows(data);
      if (!contentRange) setTotal(data.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [gameType, category, page, rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [gameType, category]);

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (rows.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
        No past games found for this game type{category ? ` and category "${category}"` : ''}.
      </Typography>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Published</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.date}</TableCell>
                <TableCell>{row.category || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.slug || '—'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.published ? 'Yes' : 'No'}
                    size="small"
                    color={row.published ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell>
                  <a href={`#/game-configs/${row.id}`} style={{ textDecoration: 'none' }}>
                    Edit
                  </a>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </Box>
  );
};
